import {useState, useEffect} from "react";
import _ from "lodash";
import {useVault} from "../top/VaultContext";
import {
	VaultEventType,
	VaultEvent,
	VaultState,
} from "../../../shared/types/VaultTypes";

type UseVaultUpdateOptions = {
	requireUser?: boolean;
};

type UseVaultUpdateProps<T> = {
	getValuesFromVault?: (vaultState: VaultState) => T;
	eventHandlers?: Record<VaultEvent[VaultEventType], (...args: any[]) => void>;
	options?: UseVaultUpdateOptions;
};

const shouldDeriveValues = (
	vaultState: VaultState,
	options?: UseVaultUpdateOptions
) => {
	return !options?.requireUser || _.get(vaultState, "user.userID");
};

export const useVaultUpdate = <T>({
	getValuesFromVault,
	eventHandlers,
	options,
}: UseVaultUpdateProps<T>) => {
	const vault = useVault();
	const [generation, setGeneration] = useState<number>(-1);
	const [values, setValues] = useState<T | null>(null);

	useEffect(() => {
		const updateValues = (
			newState: VaultState,
			newGeneration: number,
			events?: {name: VaultEventType; args: any[]}[]
		) => {
			// if the generation is older or the same as the current generation
			// nothing in the vault should have changed
			if (generation >= newGeneration) {
				return;
			}

			// update the generation
			setGeneration(newGeneration);

			// update the values derived from the vault state if they've changed
			if (getValuesFromVault) {
				setValues((currentValues: T | null) => {
					if (!shouldDeriveValues(newState, options)) {
						return null;
					}

					// get the new values from the vault and return them if they've changed
					const newValues = getValuesFromVault?.(newState);
					if (!newValues || _.isEqual(currentValues, newValues)) {
						return currentValues;
					}
					return newValues;
				});
			}

			// trigger the relevant event handlers if they exist
			if (eventHandlers && events?.length) {
				events.forEach(event => {
					const handler = eventHandlers[event.name];
					handler && handler(...event.args);
				});
			}
		};

		// call the updateValues whenever the effect triggers
		updateValues(vault?.getState(), vault?.getGeneration());

		// subscribe to the vault update event
		vault?.on("update", updateValues);
		return () => {
			vault?.off("update", updateValues);
		};
	}, [vault, getValuesFromVault, options, eventHandlers]);

	return values;
};
